/**
 * Mobile Navigation — interactive behavior.
 *
 * @description
 * Manages the full client-side interactivity for the mobile navigation
 * component rendered by ISML templates.
 *
 * ### Key Features
 * - Drilldown navigation with unlimited nesting levels
 * - Smooth level transitions for opening and closing submenus
 * - Dialog open/close with CSS slide animations and backdrop
 * - ESC key support: closes dialog at main level, navigates back in submenus
 * - Dynamic back button text: context-aware label showing the parent item name
 * - Focus management: dialog receives focus after navigation actions
 * - Accessibility: ARIA attributes (`aria-expanded`, `aria-hidden`,
 *   `aria-controls`) and disabled state management
 * - DOM restructuring: ISML renders submenus nested inside `<li>` elements,
 *   but CSS requires them as siblings of the main list. This script
 *   relocates submenu elements to `navigationWrapper` on init to isolate
 *   scroll contexts and enable independent `position: absolute` layout
 *
 * Loaded via mobile-navigation.isml.
 */
'use strict';

export function initMobileNavigation() {
    // ==================== DOM REFERENCES ====================
    const root = document.getElementById('mobile-navigation');
    if (!root) return;

    const menuButton = root.querySelector('.mobileNavigation__menuButton');
    const dialog = document.getElementById('mobile-navigation-dialog');
    const backButton = root.querySelector('.mobileNavigation__modalBackButton');
    const closeButton = root.querySelector(
        '.mobileNavigation__modalCloseButton',
    );
    const navigationWrapper = root.querySelector(
        '.mobileNavigation__navigationWrapper',
    );

    if (
        !menuButton ||
        !dialog ||
        !backButton ||
        !closeButton ||
        !navigationWrapper
    ) {
        return;
    }

    // ==================== CONSTANTS ====================
    const SUBMENU_PREFIX = 'submenu-';
    const CLOSING_CLASS = 'mobileNavigation__modal--closing';
    const LIST_ACTIVE = 'mobileNavigation__list--active';
    const LIST_ON_PATH = 'mobileNavigation__list--onPath';
    const LIST_CLOSING = 'mobileNavigation__list--closing';
    const LIST_MAIN = 'mobileNavigation__list--main';
    const BACK_HIDDEN = 'mobileNavigation__headerButton--hidden';
    const ITEM_HIDDEN = 'mobileNavigation__listItem--hidden';

    // ==================== DOM RESTRUCTURING ====================
    /**
     * Relocates submenu lists from nested `<li>` positions to direct
     * children of `navigationWrapper`.
     * ISML renders submenus nested inside their parent `<li>` elements.
     * CSS, however, expects submenu lists to be direct children of
     * `navigationWrapper` so that each submenu has an independent scroll
     * context and `position: absolute` resolves relative to the wrapper
     * rather than a scrollable parent list.
     * This IIFE moves every submenu element (marked with `data-submenu`)
     * up to `navigationWrapper`, making them siblings of the main list.
     * @private
     */
    (function _relocateSubmenus() {
        let submenu;

        // Re-query each iteration: appendChild mutates the live DOM tree,
        // and deeper submenus surface as their parents are moved.
        while (
            (submenu = navigationWrapper.querySelector(
                '.mobileNavigation__listItem > [data-submenu]',
            ))
        ) {
            navigationWrapper.appendChild(submenu);
        }
    })();

    // ==================== STATE ====================

    /**
     * Drill-down navigation state — stores the path of expanded item IDs.
     * Used to track the current position in the navigation hierarchy and
     * to determine which submenus are visible. An empty array means the
     * main (L1) level is active.
     *
     * Example: `['mobile-nav-btn-kids-1', 'mobile-nav-btn-shoes-2']`
     * means L3 (shoes submenu inside kids) is currently displayed.
     *
     * @type {string[]}
     */
    let visiblePath = [];

    /**
     * Whether the dialog closing animation is in progress.
     * Set to `true` by `startClosing`, consumed by the `animationend`
     * handler to call `closeMenu` once the slide-out animation finishes.
     *
     * @type {boolean}
     */
    let isClosingDialog = false;

    /**
     * Original back button label captured before any dynamic updates.
     * Captured once on init from the server-rendered text content,
     * then used as the fallback when no parent label is available.
     *
     * @type {string}
     */
    const defaultBackText =
        (backButton.textContent && backButton.textContent.trim()) || 'Back';

    // ==================== HELPERS ====================

    /**
     * Get the main (L1) list element.
     *
     * @returns {Element|null}
     */
    /** @private */
    function _getMainList() {
        return navigationWrapper.querySelector(`.${LIST_MAIN}`);
    }

    /**
     * Get a submenu element by item ID.
     * @param {string} itemId - Navigation item ID
     * @returns {Element|null}
     * @private
     */
    function _getSubmenuById(itemId) {
        return navigationWrapper.querySelector(`#${SUBMENU_PREFIX}${itemId}`);
    }

    /**
     * Get all submenu elements (marked with `data-submenu`).
     * @returns {NodeList}
     * @private
     */
    function _getAllSubmenus() {
        return navigationWrapper.querySelectorAll('[data-submenu]');
    }

    /**
     * Check if currently at main (L1) level.
     * @returns {boolean}
     * @private
     */
    function _isMainLevel() {
        return visiblePath.length === 0;
    }

    // ==================== VISIBILITY ====================

    /**
     * Synchronizes expansion state for all navigation buttons.
     * Tracks which items are expanded (present in `visiblePath`) and
     * updates their `aria-expanded` attributes and `disabled` state
     * accordingly. Expanded buttons are disabled to prevent duplicate
     * navigation into an already-visible submenu.
     * @private
     */
    function _updateButtonStates() {
        const buttons = navigationWrapper.querySelectorAll(
            '.mobileNavigation__navigationButton',
        );

        buttons.forEach((button) => {
            const listItem = button.closest('.mobileNavigation__listItem');
            if (!listItem) return;

            const itemId = listItem.getAttribute('id');
            const isExpanded = visiblePath.includes(itemId);

            if (button.hasAttribute('aria-expanded')) {
                button.setAttribute(
                    'aria-expanded',
                    isExpanded ? 'true' : 'false',
                );
            }

            button.disabled = isExpanded;
        });
    }

    /**
     * Updates `aria-hidden` and visibility class on individual list items.
     * An item is visible when:
     * - Its parent list is the currently active level (`--active`), OR
     * - The item's ID is explicitly included in `visiblePath` (the item
     * has an expanded submenu and must remain accessible)
     * Items inside custom lists (`--custom`) are skipped — they follow
     * their parent's visibility and are always interactive.
     * A hidden item gets the `--hidden` class (for CSS `visibility: hidden`)
     * and `aria-hidden="true"` so it is removed from the accessibility tree.
     * @private
     */
    function _updateItemVisibility() {
        const allItems = navigationWrapper.querySelectorAll(
            '.mobileNavigation__listItem',
        );

        allItems.forEach((item) => {
            const itemId = item.getAttribute('id');
            if (!itemId) return;

            // Skip items inside custom lists — they follow parent visibility
            const parentList = item.closest('.mobileNavigation__list');
            if (
                parentList &&
                parentList.classList.contains('mobileNavigation__list--custom')
            ) {
                return;
            }

            const isParentActive =
                parentList && parentList.classList.contains(LIST_ACTIVE);
            const isOnPath = visiblePath.includes(itemId);

            if (isParentActive || isOnPath) {
                item.classList.remove(ITEM_HIDDEN);
                item.removeAttribute('aria-hidden');
            } else {
                item.classList.add(ITEM_HIDDEN);
                item.setAttribute('aria-hidden', 'true');
            }
        });
    }

    /**
     * Determines the text label for the back button based on the
     * current navigation path.
     * Looks up the parent navigation item (one level up from the
     * current depth) in the DOM and extracts its visible text from
     * the trigger element (button or link). If the parent item or
     * its text cannot be found, falls back to {@link defaultBackText}.
     * @returns {string} Parent item label, or default fallback text
     * @accessibility
     * Ensures the back button always has a meaningful accessible label
     * reflecting the parent menu context, improving orientation for
     * screen reader and keyboard users navigating deep menu structures.
     * @private
     */
    function _getBackButtonText() {
        // At root or first submenu level, no parent label available
        if (visiblePath.length <= 1) return defaultBackText;

        // Parent is the second-to-last item in the path
        const parentId = visiblePath[visiblePath.length - 2];
        if (!parentId) return defaultBackText;

        const parentElement = document.getElementById(parentId);
        if (!parentElement) return defaultBackText;

        const trigger = parentElement.querySelector(
            '.mobileNavigation__navigationButton, .mobileNavigation__listItemLink',
        );
        if (!trigger) return defaultBackText;

        // Extract text content (handles elements with icon + text span)
        const textSpan = trigger.querySelector('span');
        const text = textSpan ? textSpan.textContent : trigger.textContent;

        return (text && text.trim()) || defaultBackText;
    }

    /**
     * Updates the back button's visibility, disabled state, and label.
     * At the main level (L1) the button is hidden and disabled since
     * there is no parent level to return to. At deeper levels it
     * becomes visible and its text is dynamically set to the parent
     * item's label via {@link getBackButtonText}.
     * @private
     */
    function _updateBackButton() {
        if (_isMainLevel()) {
            backButton.classList.add(BACK_HIDDEN);
            backButton.disabled = true;
        } else {
            backButton.classList.remove(BACK_HIDDEN);
            backButton.disabled = false;

            const backText = _getBackButtonText();
            backButton.textContent = backText;
        }
    }

    /**
     * Core visibility synchronization — updates all list CSS classes,
     * ARIA attributes, item visibility, back button, and button states
     * based on the current `visiblePath`.
     * Called after every navigation action (drill down, back, close).
     * Determines each list's state by comparing its item ID against
     * `visiblePath`:
     * - **Main list (L1):** active when `visiblePath` is empty (no
     * submenu open). Marked as `--onPath` when a deeper level is
     * displayed, so it stays in view but is not scrollable/interactive.
     * - **Submenu:** *active* when it is the last item in `visiblePath`
     * (the level currently shown to the user). *On-path* when it is an
     * ancestor of the active level — it stays translated to `0` so the
     * layered navigation effect works, but is not scrollable.
     * - **Inactive submenus:** neither active nor on-path — hidden via
     * `aria-hidden="true"` and translated off-screen.
     * @private
     */
    function _updateListVisibility() {
        const mainList = _getMainList();
        const allSubmenus = _getAllSubmenus();

        // Main list: active at L1, on-path when any submenu is open
        if (mainList) {
            if (visiblePath.length === 0) {
                mainList.classList.add(LIST_ACTIVE);
                mainList.classList.remove(LIST_ON_PATH);
                mainList.removeAttribute('aria-hidden');
            } else {
                mainList.classList.remove(LIST_ACTIVE);
                mainList.classList.add(LIST_ON_PATH);
                mainList.removeAttribute('aria-hidden');
            }
        }

        allSubmenus.forEach((submenu) => {
            const submenuId = submenu.getAttribute('id');
            if (!submenuId) return;

            // Extract item ID from submenu ID (e.g., "submenu-item-123" → "item-123")
            const itemId = submenuId.replace(SUBMENU_PREFIX, '');
            const pathIndex = visiblePath.indexOf(itemId);

            // On-path: ancestor of the currently active level
            const isOnPath =
                pathIndex >= 0 && pathIndex < visiblePath.length - 1;
            // Active: the level currently displayed to the user
            const isActive =
                pathIndex >= 0 && pathIndex === visiblePath.length - 1;

            if (isActive) {
                submenu.classList.add(LIST_ACTIVE);
                submenu.classList.remove(LIST_ON_PATH);
                submenu.setAttribute('aria-hidden', 'false');
            } else if (isOnPath) {
                submenu.classList.remove(LIST_ACTIVE);
                submenu.classList.add(LIST_ON_PATH);
                submenu.setAttribute('aria-hidden', 'false');
            } else {
                submenu.classList.remove(LIST_ACTIVE, LIST_ON_PATH);
                submenu.setAttribute('aria-hidden', 'true');
            }
        });

        _updateItemVisibility();
        _updateBackButton();
        _updateButtonStates();
    }

    // ==================== NAVIGATION ====================

    /**
     * Navigates into a submenu by adding the item to the visible path.
     * Appends `itemId` to `visiblePath`, then synchronizes all DOM
     * states via `updateListVisibility`. The new submenu slides in
     * from the right via CSS transition on the `--active` class.
     * @param {string} itemId - ID of the item whose submenu to open
     * @private
     */
    function _navigateToSubMenu(itemId) {
        // Update navigation state to show the new submenu
        visiblePath.push(itemId);
        _updateListVisibility();

        // Return focus to dialog to ensure ESC key handling works properly
        dialog.focus({ preventScroll: true });
    }

    /**
     * Navigates back to the previous level by removing the last item
     * from `visiblePath`.
     * Called by the back button click handler and by the ESC key
     * handler when the user presses Escape inside a submenu.
     * Before updating the path, the closing submenu receives the
     * `--closing` class to trigger a slide-out-to-right exit animation.
     * A one-time `animationend` listener removes the class once the
     * animation completes, preventing stale state.
     * @private
     */
    function _navigateBack() {
        if (visiblePath.length === 0) return;

        const closingItemId = visiblePath[visiblePath.length - 1];
        const closingSubmenu = _getSubmenuById(closingItemId);

        // Set closing state to trigger exit animation (slideOutRight)
        if (closingSubmenu) {
            closingSubmenu.classList.add(LIST_CLOSING);

            // Clean up closing class after animation completes
            closingSubmenu.addEventListener('animationend', function onEnd(e) {
                if (e.target !== e.currentTarget) return;
                closingSubmenu.classList.remove(LIST_CLOSING);
                closingSubmenu.removeEventListener('animationend', onEnd);
            });
        }

        visiblePath.pop();
        _updateListVisibility();

        // Return focus to dialog to ensure ESC key handling works properly
        dialog.focus({ preventScroll: true });
    }

    // ==================== DIALOG ====================

    /**
     * Opens the navigation dialog modal.
     * Resets the closing flag, shows the native `<dialog>` as a modal
     * (which enables backdrop and focus trapping), disables the menu
     * button to prevent duplicate opens, and enables the close button.
     * @private
     */
    function _showMenu() {
        isClosingDialog = false;
        dialog.showModal();
        menuButton.disabled = true;
        menuButton.setAttribute('aria-expanded', 'true');
        closeButton.disabled = false;
    }

    /**
     * Initiates the dialog closing animation.
     * Sets `isClosingDialog` to `true` and adds the `--closing` CSS
     * class which triggers the `slides-out-from-the-left` animation.
     * The actual dialog close happens in {@link closeMenu}, called by
     * the `animationend` handler once the animation completes.
     * @private
     */
    function _startClosing() {
        isClosingDialog = true;
        dialog.classList.add(CLOSING_CLASS);
    }

    /**
     * Resets all menu-related state to the initial (closed) configuration.
     * Shared cleanup logic used by both {@link closeMenu} (after closing
     * animation completes) and the native `close` event fallback handler.
     * Performs the following:
     * 1. Removes the dialog closing CSS class
     * 2. Clears any lingering submenu closing animations
     * 3. Resets navigation path to main level
     * 4. Synchronizes all DOM visibility states
     * 5. Re-enables the hamburger menu button
     * 6. Disables the close button (dialog is no longer open)
     * @private
     */
    function _resetMenuState() {
        dialog.classList.remove(CLOSING_CLASS);

        _getAllSubmenus().forEach((submenu) => {
            submenu.classList.remove(LIST_CLOSING);
        });

        // Reset navigation state to main level
        visiblePath = [];
        _updateListVisibility();

        menuButton.disabled = false;
        menuButton.setAttribute('aria-expanded', 'false');
        closeButton.disabled = true;
    }

    /**
     * Completes the dialog close after the exit animation finishes.
     * Called by the `animationend` handler when `isClosingDialog` is
     * `true`. Closes the native `<dialog>` element, resets the closing
     * flag, and delegates all remaining cleanup to {@link resetMenuState}.
     * @private
     */
    function _closeMenu() {
        dialog.close();
        isClosingDialog = false;
        _resetMenuState();
    }

    // ==================== EVENT LISTENERS ====================

    // Open menu
    menuButton.addEventListener('click', _showMenu);

    // Close menu
    closeButton.addEventListener('click', _startClosing);

    // Back button
    backButton.addEventListener('click', _navigateBack);

    /**
     * Handles `animationend` on the dialog element.
     *
     * Fires after both the opening (`slides-in-from-the-left`) and
     * closing (`slides-out-from-the-left`) CSS animations complete.
     * Only the closing case needs action — call `closeMenu` to
     * finalize the dialog close and reset state.
     *
     * Ignores events bubbling from child elements (e.g. submenu
     * slide animations) by checking `e.target === dialog`.
     */
    dialog.addEventListener('animationend', (e) => {
        if (e.target !== dialog) return;

        if (isClosingDialog) {
            _closeMenu();
        }
    });

    /**
     * Fallback handler for the native dialog `close` event.
     *
     * When the dialog is closed directly (e.g. via backdrop click
     * or browser-initiated close) bypassing the internal flow of
     * `startClosing` → animation → `closeMenu`, `isClosingDialog`
     * is `false`. In that case, reset all states immediately.
     *
     * When `isClosingDialog` is `true`, `closeMenu` has already
     * handled the cleanup — skip to avoid double-resetting.
     */
    dialog.addEventListener('close', () => {
        if (!isClosingDialog) {
            _resetMenuState();
        }
    });

    /**
     * Handles ESC key press events on the dialog.
     *
     * Prevents the browser's default dialog close behavior and
     * executes different actions based on the current navigation state:
     * - At the main level (no submenu open): initiates dialog close
     *   with exit animation via `startClosing`
     * - In a submenu (deeper level): navigates back to the previous
     *   level via `navigateBack`
     *
     * `stopPropagation` ensures the event is handled only once and
     * does not reach parent listeners.
     */
    dialog.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();

            if (_isMainLevel()) {
                _startClosing();
            } else {
                _navigateBack();
            }
        }
    });

    /**
     * Handles navigation button clicks via event delegation on the
     * navigation wrapper.
     *
     * Instead of attaching individual listeners to every button
     * (which would be fragile given the DOM restructuring performed
     * on init), a single listener on the wrapper catches all clicks
     * and identifies the target button via `closest`.
     *
     * Only triggers submenu navigation if a matching submenu element
     * exists for the clicked item's ID.
     */
    navigationWrapper.addEventListener('click', (e) => {
        const button = e.target.closest('.mobileNavigation__navigationButton');
        if (!button) return;

        const listItem = button.closest('.mobileNavigation__listItem');
        if (!listItem) return;

        const itemId = listItem.getAttribute('id');
        if (!itemId) return;

        const submenu = _getSubmenuById(itemId);
        if (submenu) {
            _navigateToSubMenu(itemId);
        }
    });

    // Enable hamburger button
    menuButton.disabled = false;

    // Set initial visibility
    _updateListVisibility();

    /**
     * Public API consumed by sibling scripts that need to drive the
     * Mobile Navigation dialog programmatically (e.g. the close-then-reopen
     * flow in `mobile-navigation-modals.js`).
     *
     * Stable surface — do not rename properties without updating consumers.
     *
     * @typedef {Object} MobileNavigationApi
     * @property {() => void} close - Initiates the slide-out closing animation
     *   (delegates to `startClosing`). The dialog itself is closed by the
     *   `animationend` handler.
     * @property {() => void} open - Opens the dialog as a modal
     *   (delegates to `showMenu`).
     * @property {() => string[]} getVisiblePath - Returns a copy of the
     *   current drilldown path (item IDs from L1 down to the visible level).
     * @property {(path: string[]) => void} setVisiblePath - Replaces the
     *   drilldown path and re-syncs all DOM visibility/aria state.
     * @property {() => HTMLDialogElement} getDialog - Returns the underlying
     *   `<dialog>` element so callers can attach `animationend` listeners.
     */

    /** @type {MobileNavigationApi} */
    window.MobileNavigation = {
        close: _startClosing,
        open: _showMenu,
        getVisiblePath: () => visiblePath.slice(),
        setVisiblePath: (path) => {
            visiblePath = Array.isArray(path) ? path.slice() : [];
            _updateListVisibility();
        },
        getDialog: () => dialog,
    };
}
